import { router } from "@inertiajs/react"
import { AlertCircle, Plug } from "lucide-react"
import { type FormEvent, useState } from "react"

import calComIcon from "@/assets/cal_com.svg"
import calendlyIcon from "@/assets/calendly.svg"
import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import type {
  CalendarKind,
  SetupStepProps,
} from "@/components/agents/setup/types"
import { YesNoCards } from "@/components/agents/setup/yes-no-cards"
import { FormField } from "@/components/form-field"
import {
  type PatCredentials,
  PatCredentialsForm,
} from "@/components/integrations/pat-credentials-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  settingsIntegrationCredentialConnectPath,
  settingsMcpConnectPath,
  setupAgentPath,
} from "@/routes"

const K_ENABLED = "appointments_enabled"
const K_KIND = "appointment_calendar_kind"
const K_EVENT_TYPE_URI = "appointment_event_type_uri"

// IMP-168: only providers that can book a verified slot live on the call.
// Google Calendar + Outlook are removed (raw calendars, no bookable-slot
// concept) until the scheduling engine exists.
const CALENDAR_OPTIONS: { id: CalendarKind; label: string; logo: string }[] = [
  { id: "cal_com", label: "Cal.com", logo: calComIcon },
  { id: "calendly", label: "Calendly", logo: calendlyIcon },
]

function readBool(data: Record<string, unknown>, key: string): boolean {
  return data[key] === true
}

function readString(data: Record<string, unknown>, key: string): string {
  const v = data[key]
  return typeof v === "string" ? v : ""
}

function readKind(data: Record<string, unknown>): CalendarKind | "" {
  const v = data[K_KIND]
  return v === "cal_com" || v === "calendly" ? v : ""
}

export function AppointmentsStep({
  agent,
  stepNumber,
  totalSteps,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
}: SetupStepProps) {
  const [enabled, setEnabled] = useState(() =>
    readBool(agent.setup_data, K_ENABLED),
  )
  const [kind, setKind] = useState<CalendarKind | "">(() => {
    const persisted = readKind(agent.setup_data)
    // IMP-321 (Chris's feedback): the redirect-out connect flow pre-saves the
    // chosen kind BEFORE the OAuth handshake (persistIntentThenRedirect) so a
    // successful return lands pre-selected. But if the user cancels or the
    // connection fails, that persisted kind would otherwise leave the tile
    // looking selected — with its conditional fields — even though nothing
    // connected. Only adopt it when the calendar is actually connected.
    return persisted && connectedCalendars[persisted] ? persisted : ""
  })
  const [eventTypeUri, setEventTypeUri] = useState(() =>
    readString(agent.setup_data, K_EVENT_TYPE_URI),
  )
  const [processing, setProcessing] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [calComDialogOpen, setCalComDialogOpen] = useState(false)

  // IMP-176 (Chris's feedback): pre-save the user's intent before a
  // redirect-out connection flow (Calendly / Google / Outlook OAuth) so
  // that when they land back on the wizard, the "Book appointments"
  // toggle is already ON and the calendar tile they just connected is
  // pre-selected. Without this, the user comes back to a fresh-looking
  // step with the toggle off — wiping their just-completed connection
  // from the UI's perspective even though the integration is wired.
  function persistIntentThenRedirect(calKind: CalendarKind, target: string) {
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_data: {
          [K_ENABLED]: true,
          [K_KIND]: calKind,
        },
      },
      {
        preserveScroll: true,
        // Don't update the local component state — we're navigating away.
        // The next page load will read the freshly-persisted values.
        onSuccess: () => window.location.assign(target),
        // If the pre-save fails, we still send the user to OAuth — losing
        // the intent is preferable to blocking the connection entirely.
        onError: () => window.location.assign(target),
      },
    )
  }

  function connectCalendar(calKind: CalendarKind) {
    // Cal.com uses a Personal Access Token — show an inline dialog instead of
    // redirecting out of the wizard. Calendly uses the per-user MCP OAuth flow.
    if (calKind === "cal_com") {
      setCalComDialogOpen(true)
      return
    }

    const returnTo = `/agents/${agent.uid}`
    const target =
      settingsMcpConnectPath(calKind) +
      `?return_to=${encodeURIComponent(returnTo)}`
    persistIntentThenRedirect(calKind, target)
  }

  function onCalComConnected() {
    // Match the redirect-out behavior: leave the user with the toggle on
    // and Cal.com pre-selected once their PAT validates.
    setEnabled(true)
    setKind("cal_com")
    setCalComDialogOpen(false)
  }

  function submit(nextStep: "step-messages" | "step-transfers") {
    setProcessing(true)
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_step: nextStep,
        setup_data: {
          [K_ENABLED]: enabled,
          [K_KIND]: enabled ? kind : "",
          [K_EVENT_TYPE_URI]: enabled ? eventTypeUri : "",
        },
      },
      {
        preserveScroll: true,
        onError: (errors) => setFieldErrors(errors as Record<string, string>),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const connected = kind !== "" && connectedCalendars[kind]
  // IMP-168: the connected provider's live event types. A chosen event type is
  // mandatory — there is no free-text fallback.
  const eventTypes =
    kind === "calendly"
      ? calendlyEventTypes.map((et) => ({ value: et.uri, et }))
      : kind === "cal_com"
        ? calComEventTypes.map((et) => ({ value: String(et.id), et }))
        : []
  const hasEventTypes = eventTypes.length > 0
  // Can only continue once a connected calendar AND a chosen event type exist
  // (or appointments are off entirely). IMP-168: a Free Calendly plan can't
  // book via the API — the server rejects the save with an event-type error, so
  // keep Continue disabled while that error stands (until the user changes the
  // calendar/event type or upgrades).
  const canContinue =
    (!enabled || (connected && eventTypeUri !== "")) &&
    !fieldErrors[K_EVENT_TYPE_URI]

  // Clearing the event-type error re-enables Continue when the user changes
  // their selection after a Free-plan rejection.
  function clearEventTypeError() {
    setFieldErrors((prev) => {
      if (!prev[K_EVENT_TYPE_URI]) return prev
      const next = { ...prev }
      delete next[K_EVENT_TYPE_URI]
      return next
    })
  }

  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={() => submit("step-messages")}>
            Back
          </Button>
          <Button
            onClick={() => submit("step-transfers")}
            disabled={processing || !canContinue}
          >
            Continue
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Should I book appointments?"
        description="When a caller wants an appointment, I can check your calendar and book a time before the call ends."
      />
      <YesNoCards
        yesLabel="Yes, book appointments"
        yesSub="I'll offer open times from your calendar and book them on the call."
        noLabel="No, don't book appointments"
        noSub="I'll answer questions, but won't book anything."
        value={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <>
          <FormField label="Calendar integration" error={fieldErrors[K_KIND]}>
            <div className="grid grid-cols-2 gap-3">
              {CALENDAR_OPTIONS.map(({ id, label, logo }) => {
                const connected = connectedCalendars[id]
                const selected = kind === id
                return (
                  <div
                    key={id}
                    onClick={() => {
                      if (connected) {
                        setKind(id)
                        clearEventTypeError()
                      }
                    }}
                    className={cn(
                      "rounded-md border p-3 transition-colors",
                      selected
                        ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                        : "border-border",
                      connected && "cursor-pointer",
                      connected && !selected && "hover:border-blue-200",
                    )}
                  >
                    <div className="mb-4 flex items-start justify-between">
                      <img src={logo} alt={label} className="size-7" />
                      {connected ? (
                        <span className="border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium">
                          Connected
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            connectCalendar(id)
                          }}
                          className="border-border hover:bg-muted flex size-6 items-center justify-center rounded-sm border transition-colors"
                          aria-label={`Connect ${label}`}
                        >
                          <Plug className="text-muted-foreground size-3.5" />
                        </button>
                      )}
                    </div>
                    <p
                      className={cn(
                        "text-sm font-medium",
                        selected && "text-blue-600 dark:text-blue-400",
                      )}
                    >
                      {label}
                    </p>
                  </div>
                )
              })}
            </div>
          </FormField>

          {/* IMP-168: a chosen event type is mandatory and picked from the
              provider's live list — no free-text fallback. */}
          {connected && hasEventTypes && (
            <FormField
              label="Event type"
              htmlFor="appointment_event_type_uri"
              error={fieldErrors[K_EVENT_TYPE_URI]}
            >
              <select
                id="appointment_event_type_uri"
                value={eventTypeUri}
                onChange={(e) => {
                  setEventTypeUri(e.target.value)
                  clearEventTypeError()
                }}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="">Choose an event type…</option>
                {eventTypes.map(({ value, et }) => (
                  <option key={value} value={value}>
                    {et.name}
                    {et.duration_minutes ? ` (${et.duration_minutes} min)` : ""}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          {connected && !hasEventTypes && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>No bookable event types</AlertTitle>
              <AlertDescription>
                Create an event type in{" "}
                {kind === "calendly" ? "Calendly" : "cal.com"} first, then come
                back and refresh — the receptionist books against a specific
                event type.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      <CalComConnectDialog
        open={calComDialogOpen}
        onOpenChange={setCalComDialogOpen}
        onConnected={onCalComConnected}
        returnTo={`/agents/${agent.uid}`}
      />
    </WizardLayout>
  )
}

// IMP-176: lets users paste their Cal.com Personal Access Token without
// leaving the wizard. POSTs to the same credential-connect endpoint the
// settings-page PatConnectDialog uses; on success, Inertia auto-reloads
// the integration props (connected_calendars + cal_com_event_types) and
// the parent flips kind to cal_com via onConnected.
function CalComConnectDialog({
  open,
  onOpenChange,
  onConnected,
  returnTo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConnected: () => void
  // IMP-176 follow-up: the wizard passes the path it wants to land
  // back on after a successful connect. handle_pat_connect honors this
  // server-side so Inertia doesn't follow a redirect to the
  // /settings/integrations page on success.
  returnTo: string
}) {
  const [credentials, setCredentials] = useState<PatCredentials>({
    personalAccessToken: "",
  })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const filled = credentials.personalAccessToken.trim() !== ""

  function reset() {
    setCredentials({ personalAccessToken: "" })
    setError(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setError(null)
    router.post(
      settingsIntegrationCredentialConnectPath("cal_com"),
      {
        personal_access_token: credentials.personalAccessToken,
        return_to: returnTo,
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          reset()
          onConnected()
        },
        onError: () => {
          setError(
            "Unable to verify token. Check that it's correct and try again.",
          )
        },
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Connect Cal.com</DialogTitle>
            <DialogDescription>
              Paste your Cal.com Personal Access Token. We&rsquo;ll use it to
              pull your event types and book appointments on your behalf.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Connection failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <PatCredentialsForm
              values={credentials}
              onChange={setCredentials}
              idPrefix="cal-com-cred"
              helpText="Generate this in Cal.com → Settings → Developer → API keys."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!filled || processing}
              loading={processing}
            >
              {processing ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
